import {
  useMutation,
  type UseMutationOptions,
  useQueryClient
} from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";

import type { DeleteProductRequestDto } from "../../dtos/delete-product-request-dto";
import type { GetProductListResponseDto } from "../../dtos/get-product-list-response-dto";
import { ProductQueryKeys } from "../../generators/product-query-keys";
import { ProductService } from "../../services/product-service";

export type UseDeleteProductMutationParams = {
  options?: UseMutationOptions<
    { success: boolean },
    unknown,
    DeleteProductRequestDto
  >;
};

export const useDeleteProductMutation = ({
  options
}: UseDeleteProductMutationParams = {}) => {
  const queryClient = useQueryClient();
  const { q, page, sort } = useSearch({ from: "/products/list/" });
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (payload) => {
      return await ProductService.deleteProduct({
        payload: {
          productId: payload.productId
        }
      });
    },
    onSuccess: async (...args) => {
      const currentPageKey = ProductQueryKeys.getProductList({
        limit: 10,
        page,
        q,
        sort
      }).queryKey;

      // Invalidate and refetch current page
      await queryClient.invalidateQueries({ queryKey: currentPageKey });
      const newData = (
        queryClient.getQueryData(currentPageKey) as GetProductListResponseDto
      ).data;

      // If the page is empty and it's not the first page, go back one page
      if (Array.isArray(newData) && newData.length === 0 && page > 1) {
        const previousPageKey = ProductQueryKeys.getProductList({
          limit: 10,
          page: page - 1,
          q,
          sort
        }).queryKey;
        await queryClient.invalidateQueries({ queryKey: previousPageKey });

        navigate({
          to: "/products/list",
          search: {
            q,
            page: page - 1
          }
        });
      }

      options?.onSuccess?.(...args);
    },
    ...options
  });
};
